import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Target, Wrench, Store, MapPin } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, serviceSchema } from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Btn, Overline } from "@/components/Btn";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CtaBand } from "@/components/CtaBand";
import { getSecteur, SECTEURS } from "@/data/secteurs";
import { getService } from "@/data/services";
import { getLocale } from "@/data/locales";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function SectorPage() {
  const { slug } = useParams();
  const s = getSecteur(slug);
  if (!s) return <Navigate to="/404" replace />;

  const path = `/${s.slug}/`;
  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: "Secteurs", to: "/secteurs/" },
    { label: s.label, to: path },
  ];
  const others = SECTEURS.filter((x) => x.slug !== s.slug).slice(0, 4);

  return (
    <>
      <Seo
        title={s.title}
        description={s.metaDescription}
        path={path}
        image={s.image}
        jsonLd={[serviceSchema(`Enseigne ${s.label}`, s.metaDescription, path), breadcrumbSchema(crumbs), faqSchema(s.faq)]}
      />

      <section className="relative overflow-hidden border-b border-white/10 bg-ink-900" data-testid="sector-hero">
        <div className="absolute inset-0 z-0">
          <img src={s.image} alt={s.h1} className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/80 to-ink-900/55" />
          <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <Breadcrumb items={crumbs} />
          <Reveal className="mt-8 max-w-3xl">
            <Overline>{s.overline}</Overline>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-[0.98]">{s.h1}</h1>
            {s.intro.map((p, i) => (
              <p key={i} className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">{p}</p>
            ))}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Btn to="/contact/" size="lg" data-testid="sector-devis-btn">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
              <Btn to="/realisations/" variant="outline" size="lg">Voir nos réalisations</Btn>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3"><Target className="h-6 w-6 text-signature" /> Vos besoins spécifiques</h2>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {s.needs.map((n) => (
                  <li key={n} className="flex items-start gap-3 rounded-lg border border-white/10 bg-ink-800 p-4 text-sm text-slate-300">
                    <Check className="h-5 w-5 text-signature shrink-0" /> {n}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3"><Wrench className="h-6 w-6 text-signature" /> Solutions recommandées</h2>
              <Stagger className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {s.solutions.map((sl) => {
                  const svc = getService(sl);
                  if (!svc) return null;
                  return (
                    <StaggerItem key={sl}>
                      <Link to={`/${svc.slug}/`} className="group flex items-center justify-between rounded-xl border border-white/10 bg-ink-800 p-5 hover:border-signature/40 transition-all" data-testid={`sector-solution-${sl}`}>
                        <div>
                          <h3 className="text-base font-semibold text-white group-hover:text-signature transition-colors">{svc.nav}</h3>
                          <p className="mt-1 text-xs text-slate-400 line-clamp-1">{svc.overline}</p>
                        </div>
                        <ArrowUpRight className="h-5 w-5 text-chrome-mid group-hover:text-signature transition-colors shrink-0" />
                      </Link>
                    </StaggerItem>
                  );
                })}
              </Stagger>
            </Reveal>

            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Exemples de réalisations</h2>
              <div className="mt-5 flex flex-wrap gap-2">
                {s.examples.map((e) => (
                  <span key={e} className="rounded-lg border border-white/10 bg-ink-800 px-4 py-2 text-sm text-slate-300">{e}</span>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5">Questions fréquentes</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {s.faq.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} className="rounded-xl border border-white/10 bg-ink-800 px-5" data-testid={`sector-faq-${i}`}>
                    <AccordionTrigger className="text-left text-white hover:no-underline py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-400 pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-2xl border border-signature/30 bg-gradient-to-b from-ink-800 to-ink-900 p-7 glow-red">
                <h3 className="text-xl font-bold text-white">Votre enseigne {s.label.toLowerCase()}</h3>
                <p className="mt-3 text-sm text-slate-300">Étude, fabrication et pose par un seul interlocuteur. Devis gratuit et sans engagement.</p>
                <Btn to="/contact/" className="mt-5 w-full">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
              </div>

              <div className="rounded-2xl border border-white/10 bg-ink-800 p-7">
                <p className="text-xs font-mono uppercase tracking-wider text-signature mb-4">Autres secteurs</p>
                <ul className="space-y-1">
                  {others.map((o) => (
                    <li key={o.slug}>
                      <Link to={`/${o.slug}/`} className="group flex items-center justify-between rounded-md px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                        <span className="flex items-center gap-2"><Store className="h-4 w-4 text-chrome-dark group-hover:text-signature" /> {o.label}</span>
                        <ArrowUpRight className="h-4 w-4 text-chrome-dark group-hover:text-signature" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {s.relatedLocales && (
                <div className="rounded-2xl border border-white/10 bg-ink-800 p-7">
                  <p className="text-xs font-mono uppercase tracking-wider text-signature mb-4">Zones d'intervention</p>
                  <ul className="space-y-1">
                    {s.relatedLocales.map((l) => {
                      const loc = getLocale(l);
                      if (!loc) return null;
                      return (
                        <li key={l}>
                          <Link to={`/${loc.slug}/`} className="group flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                            <MapPin className="h-4 w-4 text-chrome-dark group-hover:text-signature" /> {loc.nav}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      <CtaBand title={`Un projet d'enseigne pour votre ${s.label.toLowerCase().replace(/s$/, "")} ?`} />
    </>
  );
}
